import { useMemo, useState } from "react";
import { Button, InputNumber } from "antd";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { CloseOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import {
  getCodeKey,
  LicenseCodeSelectTable,
} from "../../../global/components/LicenseCodeSelectTable";
import usePost from "../../../global/hooks/usePost";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";

const MAX_COUNT = 9999;
const getColumns = ({ selectedRowKeys, counts, onCountChange }) => [
  {
    title: "Item Count",
    key: "ItemCount",
    width: 90,
    render: (_, record) => {
      const key = getCodeKey(record);
      return (
        <InputNumber
          size="small"
          min={1}
          max={MAX_COUNT}
          precision={0}
          value={counts[key] ?? 1}
          disabled={!selectedRowKeys.includes(key)}
          onChange={(value) => onCountChange(key, value)}
          className="w-[70px]"
        />
      );
    },
  },
];

export const PurchaseItemForm = ({
  onCancel,
  productionRefetch,
  purchaseRefetch,
  existingItems = [],
}) => {
  const { id } = useParams();
  const [selectedRows, setSelectedRows] = useState([]);
  const selectedRowKeys = selectedRows.map((row) => row.key);
  const [counts, setCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const postUrl = `${process.env.REACT_APP_API_URL}/api/purchases/${id}/items/`;
  const { postData: postAction } = usePost(postUrl);

  const usedCodeNos = useMemo(
    () =>
      new Set(
        existingItems
          .map((item) => item?.CodeNo)
          .filter((codeNo) => codeNo != null)
          .map(String),
      ),
    [existingItems],
  );

  const isSelected = (record) => selectedRowKeys.includes(getCodeKey(record));

  const getDisabledReason = (record) =>
    usedCodeNos.has(getCodeKey(record))
      ? "This code is already added to this purchase."
      : null;

  const onToggle = (record) =>
    setSelectedRows((prev) =>
      prev.some((row) => row.key === record.key)
        ? prev.filter((row) => row.key !== record.key)
        : [...prev, record],
    );

  const onCountChange = (key, value) =>
    setCounts((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async () => {
    if (!selectedRows.length) {
      openErrorNotification("Please select at least one item.");
      return;
    }
    setSubmitting(true);
    const failedRows = [];
    for (const row of selectedRows) {
      const result = await postAction({
        CodeNo: Number(row.CodeNo),
        ItemCount: Number(counts[row.key]) || 1,
      });
      if (result == null) failedRows.push(row);
    }
    setSubmitting(false);
    productionRefetch?.();
    purchaseRefetch?.();

    if (!failedRows.length) {
      openConfirmNotification("Success");
      onCancel();
      return;
    }
    setSelectedRows(failedRows);
    openErrorNotification(
      failedRows.length === selectedRows.length
        ? "Something went wrong"
        : `${selectedRows.length - failedRows.length} added, ${failedRows.length} failed.`,
    );
  };
  return (
    <>
      <div className="prepurchased-items-form">
        <LicenseCodeSelectTable
          columns={getColumns({ selectedRowKeys, counts, onCountChange })}
          isSelected={isSelected}
          getDisabledReason={getDisabledReason}
          onToggle={onToggle}
        />
      </div>

      <div className="flex justify-center mt-4 gap-4">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          loading={submitting}
          disabled={submitting}
          onClick={onSubmit}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          onClick={onCancel}
          disabled={submitting}
          className="reset-button w-[104px] h-[30px] font-medium"
        >
          <CloseOutlined className="font-medium" />
          Close
        </Button>
      </div>
    </>
  );
};
